import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Paper,
  useTheme,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useLoginContext } from '../../../../../contexts/LoginContext';
import { StepperComponentProps } from '../../types';
import { useBaseStepperContext } from '../../../shared/contexts/BaseStepperContext';
import { stepperStyles } from '../../../shared/styles';

const NewUserForm: React.FC<StepperComponentProps> = ({
  description,
  label,
  stepIndex,
}) => {
  const { user, setError, setUser } = useLoginContext();
  const { updateStepValidation } = useBaseStepperContext();
  const theme = useTheme();
  const [isEmailValid, setIsEmailValid] = React.useState(true);

  // Use stepIndex prop or default to 0 for backward compatibility
  const validationStepIndex = stepIndex !== undefined ? stepIndex : 0;

  const getMissingFields = React.useCallback(() => {
    if (!user) return [];

    const missingFields = [];
    if (!user.name) missingFields.push('Full Name');
    if (!user.emailAddress || !isEmailValid)
      missingFields.push('Email Address');
    if (!user.confirmEmail || user.confirmEmail !== user.emailAddress) {
      missingFields.push('Confirm Email');
    }
    if (!user.password) missingFields.push('Password');
    if (!user.passwordConfirm || user.passwordConfirm !== user.password) {
      missingFields.push('Confirm Password');
    }
    return missingFields;
  }, [user, isEmailValid]);

  const missingFields = React.useMemo(
    () => getMissingFields(),
    [getMissingFields]
  );
  const hasErrors = React.useMemo(
    () => missingFields.length > 0,
    [missingFields]
  );

  const warningMessage = React.useMemo(() => {
    if (missingFields.length === 0) return '';
    if (missingFields.length === 1)
      return `Please complete: ${missingFields[0]}`;
    if (missingFields.length === 2)
      return `Please complete: ${missingFields.join(' and ')}`;
    return `Please complete: ${missingFields.slice(0, -1).join(', ')}, and ${
      missingFields[missingFields.length - 1]
    }`;
  }, [missingFields]);

  // Update step validation whenever validation state changes
  useEffect(() => {
    updateStepValidation(validationStepIndex, warningMessage, hasErrors);
  }, [updateStepValidation, hasErrors, warningMessage, validationStepIndex]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;
    setUser({ ...user, [name]: value });
    if (name === 'emailAddress') {
      setIsEmailValid(e.target.validity.valid);
    }
  };

  useEffect(() => {
    if (!user) {
      setError(true);
      return;
    } else {
      if (
        user.name === '' ||
        user.emailAddress === '' ||
        user.password === '' ||
        user.confirmEmail !== user.emailAddress ||
        user.passwordConfirm !== user.password
      ) {
        setError(true);
        return;
      }
    }
    if (!isEmailValid) {
      setError(true);
      return;
    }
    setError(false);
  }, [user, setError, isEmailValid]);

  if (!user) {
    return null;
  } else {
    return (
      <Box sx={stepperStyles.pageContainer}>
        <Box sx={stepperStyles.centeredContent}>
          <Typography
            variant="h5"
            component="h1"
            gutterBottom
            sx={stepperStyles.pageTitle(theme)}
          >
            {label}
          </Typography>
          <Typography variant="body1" sx={stepperStyles.pageDescription(theme)}>
            {description}
          </Typography>

          <Paper elevation={3} sx={stepperStyles.elevatedCard(theme)}>
            <form noValidate aria-label="Account creation form">
              <Box sx={stepperStyles.formContainer}>
                {/* Personal Information Section */}
                <Box>
                  <TextField
                    size="small"
                    variant="outlined"
                    required
                    fullWidth
                    label="Full Name"
                    value={user?.name}
                    name="name"
                    onChange={handleChange}
                    autoFocus
                    sx={stepperStyles.textField}
                  />
                </Box>

                {/* Email Section */}
                <Box sx={stepperStyles.inputGroup}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      required
                      fullWidth
                      label="Email Address"
                      name="emailAddress"
                      value={user?.emailAddress}
                      onChange={handleChange}
                      type="email"
                      error={!isEmailValid}
                      helperText={
                        isEmailValid || user.emailAddress === ''
                          ? ''
                          : 'Please enter a valid email address'
                      }
                      onInvalid={(e) => {
                        setIsEmailValid(false);
                      }}
                      sx={stepperStyles.textField}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      required
                      fullWidth
                      label="Confirm Email Address"
                      error={user?.confirmEmail !== user?.emailAddress}
                      helperText={
                        user?.confirmEmail !== user?.emailAddress
                          ? 'Email addresses do not match'
                          : ''
                      }
                      onChange={handleChange}
                      value={user?.confirmEmail}
                      name="confirmEmail"
                      autoComplete="off"
                      sx={stepperStyles.textField}
                    />
                  </Box>
                </Box>

                {/* Password Section */}
                <Box sx={stepperStyles.inputGroup}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      required
                      fullWidth
                      label="Password"
                      value={user?.password}
                      name="password"
                      onChange={handleChange}
                      type="password"
                      autoComplete="off"
                      sx={stepperStyles.textField}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <TextField
                      size="small"
                      variant="outlined"
                      required
                      fullWidth
                      label="Confirm Password"
                      error={user?.passwordConfirm !== user?.password}
                      helperText={
                        user?.passwordConfirm !== user?.password
                          ? 'Passwords do not match'
                          : ''
                      }
                      value={user?.passwordConfirm}
                      onChange={handleChange}
                      name="passwordConfirm"
                      type="password"
                      sx={stepperStyles.textField}
                    />
                  </Box>
                </Box>

                {/* Email Format Section */}
                <Box sx={{ mt: 3 }}>
                  <FormControl fullWidth component="fieldset">
                    <FormLabel
                      component="legend"
                      id="email-format-label"
                      sx={stepperStyles.formLabel(theme)}
                    >
                      Email Format Preference
                    </FormLabel>
                    <Typography
                      variant="caption"
                      sx={stepperStyles.captionText(theme)}
                    >
                      Choose how you'd like to receive email notifications
                    </Typography>
                    <RadioGroup
                      row
                      aria-labelledby="email-format-label"
                      name="contentType"
                      value={user?.contentType ?? 'text/plain'}
                      onChange={handleChange}
                      sx={stepperStyles.radioGroup}
                    >
                      <FormControlLabel
                        value="text/plain"
                        control={<Radio />}
                        label="Plain Text"
                      />
                      <FormControlLabel
                        value="text/html"
                        control={<Radio />}
                        label="HTML Format"
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>
            </form>
          </Paper>
        </Box>
      </Box>
    );
  }
};

export default NewUserForm;
